import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { siteInfo, TITHELY_FORM_ID } from '@/lib/utils';
import { getSettings } from '@/lib/api';

const footerLinks = {
  newHere: [
    { name: 'Plan Your Visit', href: '/plan-visit' },
    { name: 'Plan of Salvation', href: '/plan-of-salvation' },
    { name: 'Baptism', href: '/baptism' },
    { name: 'Membership', href: '/membership' },
    { name: 'Partnership', href: '/partnership' },
    { name: 'Volunteer', href: '/volunteer' },
  ],
  ministries: [
    { name: 'Kingdom Kids', href: '/ministries/kingdom-kids' },
    { name: 'Kingdom Students', href: '/ministries/kingdom-students' },
    { name: 'Worship', href: '/ministries/worship' },
    { name: "Women's Ministry", href: '/ministries/womens-ministry' },
    { name: 'Missions', href: '/ministries/missions' },
    { name: 'LifeGroups', href: '/ministries/lifegroups' },
  ],
  mainMenu: [
    { name: 'Home', href: '/' },
    { name: 'About Us', href: '/about-us' },
    { name: 'Next Steps', href: '/next-steps' },
    { name: 'Our Ministries', href: '/our-ministries' },
    { name: 'Events', href: '/events' },
    { name: 'Sermons', href: '/sermons' },
    { name: 'Giving Options', href: '/onlinegiving' },
  ],
  quickLinks: [
    { name: 'Watch Live', href: '/live' },
    { name: 'Resource Center', href: '/resources' },
    { name: 'Connect Card', href: '/connect-card' },
    { name: 'Share Testimony', href: '/share-testimony' },
    { name: 'Contact Us', href: '/contact' },
    { name: 'Leadership', href: '/leadership' },
  ],
};

export default function Footer() {
  const [settings, setSettings] = useState({ testimonial_form_visible: false });
  
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await getSettings();
        setSettings(res.data);
      } catch (error) {
        // Use defaults
      }
    };
    fetchSettings();
  }, []);

  const handleGiveOnline = () => {
    window.open(`https://give.tithe.ly/?formId=${TITHELY_FORM_ID}`, '_blank');
  };

  return (
    <footer data-testid="footer">
      {/* Map Section - no gap above */}
      <div className="h-64 md:h-80 w-full">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3365.5!2d-82.13!3d32.28!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDE2JzQ4LjAiTiA4MsKwMDcnNDguMCJX!5e0!3m2!1sen!2sus!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Church Location"
        />
      </div>

      {/* Footer Content - reduced padding */}
      <div className="bg-ht-gold">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* About Us */}
            <div>
              <h4 className="footer-title">About Us</h4>
              <p className="footer-text mb-3 text-sm">
                {siteInfo.shortName} is a kingdom-driven church in Cobbtown, Georgia serving 
                Southeast Georgia including Tattnall, Emanuel, Candler, and surrounding counties. 
                Join us Sundays at 12:00 PM!
              </p>
              <p className="footer-text mb-3 text-xs text-gray-700">
                📍 1687 Jr Collins Rd, Cobbtown, GA 30420<br />
                📞 912-237-4771 | ✉️ info@HolyTempleCCC.org
              </p>
              <button
                onClick={handleGiveOnline}
                className="btn-primary text-sm"
                data-testid="footer-give-button"
              >
                Give Online
              </button>
            </div>

            {/* New Here - conditionally show testimony link */}
            <div>
              <h4 className="footer-title">New Here?</h4>
              <nav>
                {footerLinks.newHere.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="footer-link"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Ministries */}
            <div>
              <h4 className="footer-title">Ministries</h4>
              <nav>
                {footerLinks.ministries.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="footer-link"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Main Menu */}
            <div>
              <h4 className="footer-title">Main Menu</h4>
              <nav>
                {footerLinks.mainMenu.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="footer-link"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="footer-title">Quick Links</h4>
              <nav>
                {footerLinks.quickLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="footer-link"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Copyright - reduced padding */}
        <div className="border-t border-yellow-600/30">
          <div className="container mx-auto px-4 py-3 text-center">
            <p className="text-gray-800 text-sm mb-2">
              Copyright © {new Date().getFullYear()} {siteInfo.shortName}. All Rights Reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-xs text-gray-600">
              <Link to="/privacy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link>
              <span>|</span>
              <Link to="/terms" className="hover:text-gray-900 transition-colors">Terms of Service</Link>
              <span>|</span>
              <Link to="/safety" className="hover:text-gray-900 transition-colors">Safety Policy</Link>
              <span>|</span>
              <Link to="/admin" className="hover:text-gray-900 transition-colors">Admin</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
